import { AudioSourceOptions, BASIC_TYPE, UpdateAudioSourceOptions } from 'trtc-js-sdk-core/src/enums';
import { IPlugin, Core } from 'trtc-js-sdk-core/src/plugin';
import { ValidateRule } from 'trtc-js-sdk-core/src/utils/decorators/validate';
import RtcError from '../common/rtc-error';
import { ErrorCode } from '../common/error-code';
import { isUndefined } from 'trtc-js-sdk-core/src/utils/utils';
import { MixContext, MixAudioPipeline } from 'trtc-js-sdk-core/src/manager/audio-mixer';
import kvStatManager, { KV_REPORT_KEY_AUDIO_MANAGER, KV_REPORT_VALUE_AUDIO_MANAGER_MIX } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import { INNER_EVENT } from 'trtc-js-sdk-core/src/inner-emitter/inner-type';
import { AUDIO_REFERENCE_TYPE } from './audio-processor';
import { AudioReferenceChangeType, showAutoPlayDialog } from 'trtc-js-sdk-core';
import FileDownloader from 'trtc-js-sdk-core/src/utils/file-downloader';
import { TRTCEvent } from '../trtc-event';

let axSeq = 0;
class MusicContext {
  audioObjectURL?: string;
  player?: HTMLAudioElement;
  publisher?: HTMLAudioElement;
  mixInput: MixAudioPipeline;
  constructor(options: AudioSourceOptions, mixContext: MixContext) {
    this.mixInput = new MixAudioPipeline(mixContext);
    if (options.url) {
      this.player = new Audio(options.url);
      this.player.crossOrigin = 'anonymous';
      this.publisher = new Audio(options.url);
      this.publisher.crossOrigin = 'anonymous';
      this.mixInput.replaceSource(this.publisher); // publisher 的播放由 AudioContext 接管
    } else {
      this.mixInput.replaceSource(options.track);
    }
    this.mixInput.connect();
  }
  updateSettings(options: AudioSourceOptions | UpdateAudioSourceOptions) {
    if (!this.player) return;
    if (!isUndefined(options.volume)) this.volume = options.volume;
    if (!isUndefined(options.loop)) this.loop = options.loop;
    if (!isUndefined(options.playbackRate)) this.playbackRate = options.playbackRate;
  }
  updateListener(options: AudioSourceOptions | UpdateAudioSourceOptions) {
    if (!this.player) return;

    if (options.onDurationChange) {
      const { onDurationChange } = options;
      this.player.ondurationchange = (event: any) => {
        onDurationChange(event.target.duration);
      };
    }
    if (options.onTimeUpdate) {
      const callback = options.onTimeUpdate;
      const { player } = this;
      player.ontimeupdate = () => {
        callback(player.currentTime, player.duration);
      };
    }
    if (options.onEnded) {
      this.player.onended = options.onEnded;
    }
  }
  async reload(options: AudioSourceOptions) {
    if (options.url) {
      const response = await FileDownloader.download(options.url, { retries: 3, type: 'blob' }) as Blob;
      if (this.audioObjectURL) URL.revokeObjectURL(this.audioObjectURL);
      this.audioObjectURL = URL.createObjectURL(response);
      if (this.player && this.publisher) {
        this.player.src = this.audioObjectURL;
        this.publisher.src = this.audioObjectURL;
      } else {
        this.player = new Audio(this.audioObjectURL);
        this.player.crossOrigin = 'anonymous';
        this.publisher = new Audio(this.audioObjectURL);
        this.publisher.crossOrigin = 'anonymous';
        this.mixInput.replaceSource(this.publisher);
        this.updateListener(options);
        this.updateSettings(options);
      }
    } else {
      this.mixInput.replaceSource(options.track);
    }
  }
  reset() {
    this.seek(0);
    this.mixInput.connect();
  }
  seek(time: number) {
    if (!this.player) return;
    if (time < 0 && time > this.player.duration) {
      return;
    }
    this.player.currentTime = time;
    this.publisher!.currentTime = time;
  }
  play() {
    return Promise.all([this.player?.play(), this.publisher?.play()]);
  }
  pause() {
    this.player?.pause();
    this.publisher?.pause();
  }
  stop() {
    this.player?.pause();
    this.mixInput.disconnect();
  }
  setOperation(operation: string) {
    if (operation === 'pause') {
      this.pause();
    }
    if (operation === 'resume') {
      this.pause();
      this.play();
    }
    if (operation === 'stop') {
      this.pause();
      this.seek(0);
    }
  }
  set volume(volume: number) {
    if (!this.player || !this.publisher) return;
    this.player.volume = volume;
    this.publisher.volume = volume;
  }
  set loop(loop: boolean) {
    if (!this.player || !this.publisher) return;
    this.player.loop = loop;
    this.publisher.loop = loop;
  }
  set playbackRate(rate: number) {
    if (!this.player || !this.publisher) return;
    this.player.playbackRate = rate;
    this.publisher.playbackRate = rate;
  }
}

function checkFunction(name: string, func?: any) {
  if (func && typeof func !== 'function') {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      message: `start audioMixer plugin: param ${name} should be a function.`
    });
  }
}

export class AudioMixer implements IPlugin {
  log: Core['log'];
  mixedMusicMap: Map<string, MusicContext> = new Map();
  cacheMusicMap: Map<string, MusicContext> = new Map();
  static startValidateRule: ValidateRule = {
    name: 'options',
    required: true,
    type: BASIC_TYPE.Object,
    properties: {
      id: { type: BASIC_TYPE.String, required: true },
      url: { type: BASIC_TYPE.String, required: false },
      track: { required: false },
      loop: { type: BASIC_TYPE.Boolean },
      volume: { type: BASIC_TYPE.Number }
    },
    validate(config, key: string, fnName: string) {
      if (config.url && config.url !== '*') {
        const urlWithoutParams = config.url.split('?')[0];
        const fileType = ['mp3', 'ogg', 'wav', 'flac'];
        const type = urlWithoutParams.split('.').pop();
        const isMusic = fileType.indexOf(type) >= 0 || fileType.indexOf(config.url.split('.').pop()) >= 0;
        const isBlob = urlWithoutParams.startsWith('blob');
        const isData = urlWithoutParams.startsWith('data');

        if (!(isMusic || isBlob || isData)) {
          throw new RtcError({
            code: ErrorCode.INVALID_PARAMETER,
            message: 'start audioMixer plugin: music url is invalid, please check your file format.',
            fnName
          });
        }
      }
      if (!config.url && !config.track) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          message: 'start audioMixer plugin: param url or track is required.',
          fnName
        });
      }
      checkFunction('onTimeUpdate', config.onTimeUpdate);
      checkFunction('onEnded', config.onEnded);
      checkFunction('onDurationChange', config.onDurationChange);
    }
  };
  static updateValidateRule: ValidateRule = {
    name: 'options',
    required: true,
    type: BASIC_TYPE.Object,
    properties: {
      id: { type: BASIC_TYPE.String, required: true },
      loop: { type: BASIC_TYPE.Boolean },
      volume: { type: BASIC_TYPE.Number },
      seekFrom: { type: BASIC_TYPE.Number },
      operation: { type: BASIC_TYPE.String, values: ['pause', 'resume', 'stop'] }
    },
    validate(config, key: string, fnName: string) {
      checkFunction('onTimeUpdate', config.onTimeUpdate);
      checkFunction('onEnded', config.onEnded);
      checkFunction('onDurationChange', config.onDurationChange);
    }
  };
  static stopValidateRule: ValidateRule = {
    name: 'options',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      id: { type: BASIC_TYPE.String, required: true }
    }
  };

  constructor(public core: Core) {
    axSeq = axSeq + 1;
    this.log = core.log.createChild({ id: `${this.getAlias()}${axSeq}` });
    this.log.info(`created id=${this.getAlias()}${axSeq}`);
  }

  static Name = 'AudioMixer';
  getName() {
    return AudioMixer.Name;
  }

  getAlias(): string {
    return 'ax';
  }

  getGroup(options: AudioSourceOptions | UpdateAudioSourceOptions): string {
    return options?.id;
  }

  getValidateRule(method: 'start' | 'update' | 'stop'): ValidateRule {
    switch (method) {
      case 'start':
        return AudioMixer.startValidateRule;
      case 'update':
        return AudioMixer.updateValidateRule;
      case 'stop':
        return AudioMixer.stopValidateRule;
    }
  }

  async start(options: AudioSourceOptions) {
    const { room } = this.core;
    this.core.validateSourceNode();
    this.log.info(`add music source, id: ${options.id} url: ${options.url}, track: ${options.track}`);
    const { id, url } = options;
    if (this.mixedMusicMap.has(id)) {
      return;
    }
    let music = this.cacheMusicMap.get(id);
    if (music) {
      if (options.url) {
        music.reset();
      } else {
        music.mixInput.replaceSource(options.track); // 如果用户传入新 track，则需要重新创建 audioNode；如果传入上次的 track，会命中 audioNodeCache 的缓存（不会被回收，因为用户有引用）
        music.mixInput.connect();
      }
    } else {
      music = new MusicContext(options, room.audioManager);
      this.cacheMusicMap.set(id, music);
    }
    music.updateListener(options);
    music.updateSettings(options);
    try {
      await music.play();
    } catch (error: any) {
      await this.handleAutoPlayFailed(music, options, error);
    }
    this.mixedMusicMap.set(id, music);
    if (music.mixInput.source.node) {
      // TODO: 混音插件播放的所有音频，可以在混音插件内部混合后，再取
      this.core.room.audioManager.updateAudioReference({
        type: AudioReferenceChangeType.ADD,
        audioReference: music.mixInput.source.node, // 这里不能用 music.mixInput.track, 否则是一样的引用，会导致停止混音时全部停止
        refId: `${AUDIO_REFERENCE_TYPE.AUDIO_MIXER_PLUGIN}-${id}`
      });
    }
    this.log.info(`start mix audio track ${id} success.`);
    kvStatManager.addEnum({ key: KV_REPORT_KEY_AUDIO_MANAGER.MIX, value: KV_REPORT_VALUE_AUDIO_MANAGER_MIX.MUSIC });
    this.kvUpload(options);
  }

  async handleAutoPlayFailed(music: MusicContext, options: AudioSourceOptions, error: Error) {
    const { id } = options;
    if (error.name === 'NotSupportedError') {
      // 解决 lingoAce 加载资源偶现 ERR_CACHE_OPERATION_NOT_SUPPORTED 的问题. ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022126742775
      this.log.error(`play failed, try to reload source. error: ${error}`);
      await music.reload(options);
      if (!this.mixedMusicMap.has(id)) {
        this.log.warn(`music ${id} has been stopped, skip play after reload.`);
        return;
      }
      await music.play();
    } else if (error.name === 'NotAllowedError') {
      if (this.core.room.enableAutoPlayDialog) {
        const resumePlayback = () => {
          music.play()?.finally(() => {
            innerEmitter.off(INNER_EVENT.AUTOPLAY_DIALOG_CLICK_CONFIRM, resumePlayback, this);
          });
        };
        innerEmitter.on(INNER_EVENT.AUTOPLAY_DIALOG_CLICK_CONFIRM, resumePlayback, this);
        showAutoPlayDialog();
      } else {
        // 未开启自动播放失败弹窗，抛出错误，业务侧自行处理。
        this.core.trtc.emit(TRTCEvent.AUTOPLAY_FAILED, { userId: '', mediaType: 'audio', resume: async () => music.play() });
      }
    } else {
      throw error;
    }
  }
  async update(options: UpdateAudioSourceOptions) {
    const { id, operation, seekFrom, playbackRate } = options;
    this.log.info(`update music source, ${JSON.stringify(options)}`);
    const music = this.mixedMusicMap.get(id);
    if (!music) {
      this.log.warn(`update music source failed, music id: ${id} not found.`);
      return;
    }
    music.updateSettings(options);
    music.updateListener(options);
    if (!isUndefined(operation)) music.setOperation(operation);
    if (!isUndefined(seekFrom)) music.seek(seekFrom);
    this.kvUpload(options);
  }
  async stop({ id }: UpdateAudioSourceOptions) {
    if (this.mixedMusicMap.has(id)) {
      this.log.info(`remove music source, music id: ${id}`);
      const mixedMusic = this.mixedMusicMap.get(id);
      if (mixedMusic?.mixInput.source.node) {
        this.core.room.audioManager.updateAudioReference({
          type: AudioReferenceChangeType.REMOVE,
          audioReference: mixedMusic.mixInput.source.node,
          refId: `${AUDIO_REFERENCE_TYPE.AUDIO_MIXER_PLUGIN}-${id}`
        });
      }
      mixedMusic?.stop();
      this.mixedMusicMap.delete(id);
    }
    if (id === '*') {
      this.destroyAllMusic();
    }
  }
  kvUpload(options: AudioSourceOptions & UpdateAudioSourceOptions) {
    const { track, loop, volume, playbackRate, operation, seekFrom, onTimeUpdate, onDurationChange, onEnded } = options;
    track && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_TRACK });
    loop && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_LOOP });
    volume && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_VOLUME });
    playbackRate && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_PLAYBACK_RATE });
    operation && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_OPERATION });
    seekFrom && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_SEEK });
    typeof onTimeUpdate !== 'function' && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_CURRENT_TIME });
    typeof onEnded !== 'function' && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_ENDED });
    typeof onDurationChange !== 'function' && kvStatManager.addCount({ key: KV_REPORT_KEY_AUDIO_MANAGER.USE_DURATION_CHANGE });
  }
  destroyAllMusic() {
    this.log.info('destroy all music source.');
    this.mixedMusicMap.forEach((item, id) => {
      if (item?.mixInput.track) {
        this.core.room.audioManager.updateAudioReference({
          type: AudioReferenceChangeType.REMOVE,
          audioReference: item.mixInput.track,
          refId: id
        });
      }
      this.stop({ id });
    });
  }
  destroyAllCache() {
    this.log.info('destroy all music cache.');
    this.cacheMusicMap.clear();
    // this.cacheMusicMap.forEach(item => item.mixInput.remove());
  }
  destroy() {
    this.log.info('destroy audio mixer plugin.');
    this.destroyAllMusic();
    this.destroyAllCache();
  }
}
